import { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Car, ChevronRight } from 'lucide-react';
import { CollapsibleToggle } from '../CollapsibleList';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { vehicleActivityAPI } from '../../../lib/api';
import VehicleDetailDialog from '../VehicleDetailDialog';

const EVENT_LABELS = {
    'vehicle.tap': 'Tap Visit', 'vehicle.contact_sales': 'Question', 'vehicle.sms_out': 'SMS → customer',
    'vehicle.sms_in': 'SMS ← customer', 'vehicle.test_drive_requested': 'Test drive requested',
    'vehicle.test_drive_booked': 'Test drive booked', 'vehicle.test_drive_declined': 'Test drive declined',
    'vehicle.saved': 'Saved', 'vehicle.special_offer_click': 'Offer claimed',
    // F&I desk (Phase B) + the CONFIDENTIAL insurance signal (Phase C). The
    // insurance row is a SIGNAL only — the thread itself is never reachable here.
    'vehicle.fi_calculator_opened': 'Payment calculator opened',
    'vehicle.fi_prequal_submitted': 'Financing application',
    'vehicle.fi_warranty_interest': 'Warranty interest',
    'vehicle.insurance_quote_requested': 'Insurance quote requested (confidential)',
    'vehicle.trade_in_submitted': 'Trade-in submitted',
    'vehicle.form_abandoned': 'Started a form, didn’t finish',
    'vehicle.test_drive_gift_issued': 'Test drive gift sent',
    'vehicle.review_requested': 'Review request sent',
};
const fmt = (iso) => { try { return new Date(iso).toLocaleString(); } catch { return iso; } };

/**
 * Automotive vehicle inventory — vertical panel for the SHARED Command Center
 * shell. Self-contained: loads the FULL active used-vehicle inventory (incl.
 * zero-activity vehicles) via the EXISTING vehicle-activity inventory API and
 * keeps search / filter / sort + the vehicle drill-down (timeline +
 * conversations). Extracted verbatim from the former AutomotiveCommandCenter.
 */
export default function AutomotiveInventoryPanel({ organizationId, locationIds }) {
    const { t } = useTranslation();
    const [rows, setRows] = useState([]);
    const [detail, setDetail] = useState(null);
    const [convo, setConvo] = useState(null);
    const [vq, setVq] = useState('');
    const [vfilter, setVfilter] = useState('all');
    const [vsort, setVsort] = useState('activity');
    const [collapsed, setCollapsed] = useState(true);

    const load = useCallback(async () => {
        if (!organizationId) return;
        // Inventory activity is rolled up over a 30-day window (matches the
        // shell's prior default); the LEFT-JOIN guarantees zero-activity active
        // vehicles still appear with 0 counts.
        const bv = await vehicleActivityAPI.inventory(organizationId, 30, locationIds);
        setRows(bv.data || []);
    }, [organizationId, locationIds]);
    useEffect(() => { load(); }, [load]);

    const openVehicle = async (vid) => { const r = await vehicleActivityAPI.vehicleDetail(vid); setDetail(r.data); };
    const openConvo = async (cid) => { const r = await vehicleActivityAPI.conversation(cid); setConvo(r.data); };

    const vehRows = (() => {
        const term = vq.trim().toLowerCase();
        let r = rows.filter((v) => {
            if (!term) return true;
            return `${v.stock_number || ''} ${v.year || ''} ${v.make || ''} ${v.model || ''}`.toLowerCase().includes(term);
        });
        if (vfilter === 'attention') r = r.filter((v) => (v.questions || 0) + (v.test_drives || 0) > 0);
        else if (vfilter === 'active') r = r.filter((v) => (v.total || 0) > 0);
        else if (vfilter === 'zero') r = r.filter((v) => (v.total || 0) === 0);
        const by = {
            activity: (a, b) => (b.total || 0) - (a.total || 0),
            recent: (a, b) => String(b.last_activity || '').localeCompare(String(a.last_activity || '')),
            stock: (a, b) => String(a.stock_number || '').localeCompare(String(b.stock_number || '')),
            make: (a, b) => `${a.make || ''} ${a.model || ''}`.localeCompare(`${b.make || ''} ${b.model || ''}`),
        }[vsort];
        return [...r].sort(by);
    })();

    return (
        <section className="space-y-3" data-testid="cc-automotive-inventory-panel">
            <CollapsibleToggle
                open={!collapsed}
                onToggle={() => setCollapsed((c) => !c)}
                testid="cc-veh-collapse-toggle"
                size="lg"
                icon={<Car className="w-4 h-4 text-amber-500 shrink-0" />}
                title={`${t('commandCenter.automotive.vehiclesTab', 'Vehicles')} (${rows.length})`}
            />
            {!collapsed && (<>
            <div className="flex flex-wrap items-center gap-2">
                <input value={vq} onChange={(e) => setVq(e.target.value)} data-testid="cc-veh-search"
                    placeholder={t('commandCenter.automotive.searchVehicles', 'Search stock #, make, model…')}
                    className="flex-1 min-w-[180px] rounded-lg bg-slate-100 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700 px-3 py-2 text-sm text-slate-900 dark:text-slate-100 placeholder:text-slate-500 dark:text-slate-400" />
                <Select value={vfilter} onValueChange={setVfilter}>
                    <SelectTrigger className="w-40" data-testid="cc-veh-filter"><SelectValue /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">{t('commandCenter.automotive.filterAll', 'All active')}</SelectItem>
                        <SelectItem value="attention">{t('commandCenter.automotive.filterAttention', 'Needs attention')}</SelectItem>
                        <SelectItem value="active">{t('commandCenter.automotive.filterHasActivity', 'Has activity')}</SelectItem>
                        <SelectItem value="zero">{t('commandCenter.automotive.filterNoActivity', 'No activity')}</SelectItem>
                    </SelectContent>
                </Select>
                <Select value={vsort} onValueChange={setVsort}>
                    <SelectTrigger className="w-40" data-testid="cc-veh-sort"><SelectValue /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="activity">{t('commandCenter.automotive.sortActivity', 'Most activity')}</SelectItem>
                        <SelectItem value="recent">{t('commandCenter.automotive.sortRecent', 'Recent activity')}</SelectItem>
                        <SelectItem value="stock">{t('commandCenter.automotive.sortStock', 'Stock number')}</SelectItem>
                        <SelectItem value="make">{t('commandCenter.automotive.sortMake', 'Make / model')}</SelectItem>
                    </SelectContent>
                </Select>
            </div>
            {rows.length === 0 ? <p className="text-sm text-slate-500 dark:text-slate-400" data-testid="cc-veh-empty">{t('commandCenter.automotive.noVehicles', 'No active vehicles in scope.')}</p> : (
                <div className="rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden">
                    <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-slate-100 dark:bg-slate-800/60 text-slate-500 dark:text-slate-400 text-xs"><tr>
                            <th className="text-left p-2">{t('usedVehicles.stockShort', 'Stock')}</th><th className="text-left p-2">{t('commandCenter.automotive.vehicleCol', 'Vehicle')}</th><th className="p-2">{t('commandCenter.automotive.visits', 'Visits')}</th><th className="p-2">Q</th><th className="p-2">TD</th><th className="p-2">{t('commandCenter.automotive.saves', 'Saves')}</th><th className="p-2"></th>
                        </tr></thead>
                        <tbody>
                            {vehRows.map((r) => (
                                <tr key={r.vehicle_id} className="border-t border-slate-800 hover:bg-slate-800/40 cursor-pointer" onClick={() => openVehicle(r.vehicle_id)} data-testid={`cc-vrow-${r.stock_number}`}>
                                    <td className="p-2 text-white">#{r.stock_number}</td>
                                    <td className="p-2 text-slate-600 dark:text-slate-300">{[r.year, r.make, r.model].filter(Boolean).join(' ')}</td>
                                    <td className="p-2 text-center">{r.taps}</td><td className="p-2 text-center">{r.questions}</td><td className="p-2 text-center">{r.test_drives}</td><td className="p-2 text-center">{r.saves}</td>
                                    <td className="p-2 text-right"><ChevronRight className="w-4 h-4 text-slate-500 dark:text-slate-400 inline" /></td>
                                </tr>
                            ))}
                            {vehRows.length === 0 && (
                                <tr><td colSpan={7} className="p-3 text-center text-slate-500 dark:text-slate-400 text-xs" data-testid="cc-veh-nomatch">{t('commandCenter.automotive.noMatch', 'No vehicles match your search / filter.')}</td></tr>
                            )}
                        </tbody>
                    </table>
                    </div>
                </div>
            )}
            </>)}

            {/* Vehicle drill-down (shared dialog — includes Inventory-Specials context) */}
            <VehicleDetailDialog detail={detail} onClose={() => setDetail(null)} onOpenConvo={openConvo} onActionDone={load} />

            {/* Conversation drill-down */}
            <Dialog open={!!convo} onOpenChange={(o) => !o && setConvo(null)}>
                <DialogContent className="sm:max-w-md max-h-[80vh] overflow-y-auto bg-slate-950 border-slate-800 text-slate-900 dark:text-slate-100" data-testid="cc-conversation">
                    <DialogHeader><DialogTitle className="text-white">{convo?.request && `${convo.request.year} ${convo.request.make} ${convo.request.model} · #${convo.request.stock_number}`}</DialogTitle></DialogHeader>
                    <div className="space-y-2">
                        {(convo?.messages || []).map((m) => (
                            <div key={m.id} className={`p-2.5 rounded-lg text-sm ${m.sender_role === 'sales' ? 'bg-amber-500/10 ml-6' : 'bg-slate-800 mr-6'}`}>
                                <p className="text-[11px] text-slate-500 dark:text-slate-400 mb-0.5">{m.sender_name}{m.channel === 'sms' ? ` · SMS${m.sms_status ? ' · ' + m.sms_status : ''}` : ''} · {fmt(m.created_at)}</p>
                                <p className="text-slate-900 dark:text-slate-100">{m.body}</p>
                            </div>
                        ))}
                    </div>
                </DialogContent>
            </Dialog>
        </section>
    );
}
